import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Card, CardContent } from '../../../components/ui/Card';
import { API_BASE_URL } from '../../../config/api';
import { documentService, getErrorMessage } from '../../../services/apiService';
import { useToast } from '../../../components/ui/Toast';
import {
  generateAndDownloadNDA,
  generateAndDownloadSQA,
  generateAndDownload4MDeclaration,
  generateAndDownloadCodeOfConduct,
  generateAndDownloadComplianceAgreement,
  generateAndDownloadSelfDeclaration,
  generateAndDownloadOverseasAgreement,
  generateNDABlob,
  generateSQABlob,
  generate4MDeclarationBlob,
  generateCodeOfConductBlob,
  generateComplianceAgreementBlob,
  generateSelfDeclarationBlob
} from '../../../utils/documentGenerator.jsx';

const AgreementsTab = ({ vendor }) => {
  const toast = useToast();
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal states
  const [viewAgreementModal, setViewAgreementModal] = useState({ isOpen: false, agreement: null });
  const [signatureModal, setSignatureModal] = useState({ isOpen: false, agreement: null });
  const [agreementData, setAgreementData] = useState(null);
  const [signatureData, setSignatureData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    const fetchAgreements = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch detailed agreements from the agreement-details endpoint
        let detailedAgreements = [];
        try {
          const response = await fetch(`${API_BASE_URL}/vendors/${vendor.id}/agreement-details`, {
            credentials: 'include', // Include cookies for authentication
          });

          if (response.ok) {
            const data = await response.json();
            // Handle different response structures
            detailedAgreements = Array.isArray(data) ? data : (data?.data || data?.agreements || []);
          } else if (response.status === 404) {
            // 404 is expected and acceptable - means no detailed agreements exist
            // This is normal, so we silently continue with basic agreements only
          } else {
            // Other errors (500, 403, etc.) are logged but don't block the UI
            console.warn('Failed to fetch detailed agreements:', response.status);
          }
        } catch (fetchError) {
          // Network errors or other fetch failures are logged but don't block the UI
          // We continue with basic agreements even if detailed fetch fails
          console.warn('Error fetching detailed agreements (non-critical):', fetchError);
        }

        // Create basic agreements from vendor registration data
        const basicAgreements = [];
        if (vendor.nda) {
          basicAgreements.push({
            id: 'nda-basic',
            title: 'Non-Disclosure Agreement (NDA)',
            type: 'Legal',
            status: 'Signed',
            signed_date: vendor.created_at || vendor.registration_date,
            signed_by: vendor.contact_person_name,
            valid_until: 'Perpetual',
            description: 'Basic NDA agreement signed during registration',
            version: '1.0',
            is_basic: true
          });
        }
        if (vendor.sqa) {
          basicAgreements.push({
            id: 'sqa-basic',
            title: 'Supplier Agreement',
            type: 'Quality',
            status: 'Signed',
            signed_date: vendor.created_at || vendor.registration_date,
            signed_by: vendor.contact_person_name,
            valid_until: 'Annual Renewal',
            description: 'Basic SA agreement signed during registration',
            version: '1.0',
            is_basic: true
          });
        }
        if (vendor.four_m) {
          basicAgreements.push({
            id: '4m-basic',
            title: '4M Change Management Agreement',
            type: 'Operational',
            status: 'Signed',
            signed_date: vendor.created_at || vendor.registration_date,
            signed_by: vendor.contact_person_name,
            valid_until: 'Annual Renewal',
            description: '4M Change Management agreement signed during registration',
            version: '1.0',
            is_basic: true
          });
        }
        if (vendor.code_of_conduct) {
          basicAgreements.push({
            id: 'coc-basic',
            title: 'Code of Conduct Agreement',
            type: 'Compliance',
            status: 'Signed',
            signed_date: vendor.created_at || vendor.registration_date,
            signed_by: vendor.contact_person_name,
            valid_until: 'Perpetual',
            description: 'Code of Conduct agreement signed during registration',
            version: '1.0',
            is_basic: true
          });
        }
        if (vendor.compliance_agreement) {
          basicAgreements.push({
            id: 'ca-basic',
            title: 'Compliance Agreement',
            type: 'Compliance',
            status: 'Signed',
            signed_date: vendor.created_at || vendor.registration_date,
            signed_by: vendor.contact_person_name,
            valid_until: 'Annual Renewal',
            description: 'Compliance agreement signed during registration',
            version: '1.0',
            is_basic: true
          });
        }
        if (vendor.self_declaration) {
          basicAgreements.push({
            id: 'sd-basic',
            title: 'Self Declaration',
            type: 'Declaration',
            status: 'Signed',
            signed_date: vendor.created_at || vendor.registration_date,
            signed_by: vendor.contact_person_name,
            valid_until: 'Annual Renewal',
            description: 'Self declaration signed during registration',
            version: '1.0',
            is_basic: true
          });
        }
        if (vendor.overseas_agreement) {
          basicAgreements.push({
            id: 'overseas-agreement-basic',
            title: 'Overseas Agreement',
            type: 'Legal',
            status: 'Signed',
            signed_date: vendor.created_at || vendor.registration_date,
            signed_by: vendor.contact_person_name,
            valid_until: 'Annual Renewal',
            description: 'Overseas Agreement signed during registration',
            version: '1.0',
            is_basic: true
          });
        }

        // Combine basic and detailed agreements
        const allAgreements = [...basicAgreements, ...detailedAgreements];
        setAgreements(allAgreements);

        // Don't set error if no agreements found - that's just an empty state, not an error
        // The UI will show "No agreements found" message naturally
      } catch (err) {
        // Only set error for unexpected failures that prevent us from loading basic agreements
        console.error('Failed to load agreements:', err);
        const errorMsg = getErrorMessage(err) || 'Failed to load agreements. Please try again.';
        setError(errorMsg);
      } finally {
        setLoading(false);
      }
    };

    if (vendor?.id) {
      fetchAgreements();
    }
  }, [vendor?.id]);

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'signed':
        return 'bg-emerald-100 text-emerald-800';
      case 'pending signature':
        return 'bg-amber-100 text-amber-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      case 'under review':
        return 'bg-slate-100 text-slate-800';
      default:
        return 'bg-slate-100 text-slate-800';
    }
  };

  const getTypeColor = (type) => {
    switch (type.toLowerCase()) {
      case 'legal':
        return 'bg-blue-100 text-blue-800';
      case 'quality':
        return 'bg-emerald-100 text-emerald-800';
      case 'operational':
        return 'bg-amber-100 text-amber-800';
      case 'compliance':
        return 'bg-indigo-100 text-indigo-800';
      case 'declaration':
        return 'bg-slate-100 text-slate-800';
      default:
        return 'bg-slate-100 text-slate-800';
    }
  };

  const isExpiringSoon = (validUntil) => {
    if (!validUntil || validUntil === 'Perpetual' || validUntil === 'TBD' || validUntil === 'Annual Renewal') return false;
    const expiry = new Date(validUntil.split('/').reverse().join('-'));
    const today = new Date();
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 30 && diffDays > 0;
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB'); // DD/MM/YYYY format
  };

  // Helper function to fetch digital signature and convert to base64 data URL
  const fetchDigitalSignature = async (vendorId) => {
    try {
      // Fetch all documents for the vendor using documentService
      let documents = [];
      try {
        const response = await documentService.getVendorDocuments(vendorId);
        documents = Array.isArray(response) ? response : (response?.data || response?.documents || []);
      } catch (docError) {
        console.warn('Failed to fetch vendor documents for digital signature:', docError);
        return null;
      }

      // Find the digital signature document
      const digitalSignatureDoc = documents.find(doc => doc.document_type === 'DIGITAL_SIGNATURE');

      if (!digitalSignatureDoc) {
        console.warn('Digital signature document not found for vendor');
        return null;
      }

      // Try authenticated download first, then fallback to public endpoint
      let blob;
      try {
        const downloadResponse = await documentService.downloadDocument(digitalSignatureDoc.id);
        blob = downloadResponse.data;
      } catch (authError) {
        // Fallback to public endpoint
        try {
          const downloadResponse = await fetch(`${API_BASE_URL}/documents/${digitalSignatureDoc.id}/public/download`, {
            credentials: 'include',
          });
          if (!downloadResponse.ok) {
            console.error('Failed to download digital signature:', downloadResponse.status);
            return null;
          }
          blob = await downloadResponse.blob();
        } catch (publicError) {
          console.error('Failed to download digital signature from public endpoint:', publicError);
          return null;
        }
      }

      // Convert to blob then to base64 data URL
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error fetching digital signature:', error);
      return null;
    }
  };

  // Button click handlers
  const handleViewAgreement = async (agreement) => {
    if (agreement.is_basic) {
      // For basic agreements, generate PDF and open in new tab
      try {
        setLoadingData(true);
        let vendorData = toCamelCaseObject(vendor);
        // Ensure submission date is available for document date rendering
        vendorData.submissionDate = vendor.created_at || vendor.createdAt;

        // Fetch and add digital signature
        const digitalSignature = await fetchDigitalSignature(vendor.id);
        vendorData.digitalSignature = digitalSignature;

        console.log('Generating agreement for:', agreement.id);
        console.log('Vendor data:', vendorData);

        let pdfBlob;

        if (agreement.id === 'nda-basic') {
          pdfBlob = await generateNDABlob(vendorData);
        }
        else if (agreement.id === 'sqa-basic') {
          pdfBlob = await generateSQABlob(vendorData);
        }
        else if (agreement.id === '4m-basic') {
          pdfBlob = await generate4MDeclarationBlob(vendorData);
        }
        else if (agreement.id === 'coc-basic') {
          pdfBlob = await generateCodeOfConductBlob(vendorData);
        }
        else if (agreement.id === 'ca-basic') {
          pdfBlob = await generateComplianceAgreementBlob(vendorData);
        }
        else if (agreement.id === 'sd-basic') {
          pdfBlob = await generateSelfDeclarationBlob(vendorData);
        }

        if (pdfBlob) {
          // Create object URL and open in new tab
          const url = window.URL.createObjectURL(pdfBlob);
          window.open(url, '_blank');
          // Clean up the object URL after a delay
          setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        }
      } catch (err) {
        console.error('Failed to generate agreement PDF:', err);
        toast.error('Failed to generate agreement PDF. Please try again.');
      } finally {
        setLoadingData(false);
      }
      return;
    }

    // For detailed agreements, keep the existing modal functionality
    setLoadingData(true);
    try {
      const response = await fetch(`${API_BASE_URL}/vendors/${vendor.id}/agreement-details/${agreement.id}/view`, {
        credentials: 'include', // Include cookies for authentication
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      // Handle different response structures
      const agreementData = data?.data || data;
      setAgreementData(agreementData);
      setViewAgreementModal({ isOpen: true, agreement });
    } catch (err) {
      console.error('Failed to load agreement details:', err);
      const errorMsg = getErrorMessage(err) || 'Failed to load agreement details. Please try again.';
      toast.error(errorMsg);
    } finally {
      setLoadingData(false);
    }
  };

  function toCamelCaseObject(obj) {
    const toCamel = str =>
      str.replace(/_([a-z])/g, (_, char) => char.toUpperCase());

    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [toCamel(key), value])
    );
  }

  const handleDownloadPDF = async (agreement) => {
    try {
      setLoadingData(true);

      let vendorData = toCamelCaseObject(vendor);
      // Ensure submission date is available for document date rendering
      vendorData.submissionDate = vendor.created_at || vendor.createdAt;

      // Fetch and add digital signature
      const digitalSignature = await fetchDigitalSignature(vendor.id);
      vendorData.digitalSignature = digitalSignature;

      console.log(agreement);
      if (agreement.id === 'nda-basic') {
        console.log(vendorData);
        generateAndDownloadNDA(vendorData);
      }
      else if (agreement.id === 'sqa-basic') {
        generateAndDownloadSQA(vendorData);
      }
      else if (agreement.id === '4m-basic') {
        generateAndDownload4MDeclaration(vendorData);
      }
      else if (agreement.id === 'coc-basic') {
        generateAndDownloadCodeOfConduct(vendorData);
      }
      else if (agreement.id === 'ca-basic') {
        generateAndDownloadComplianceAgreement(vendorData);
      }
      else if (agreement.id === 'sd-basic') {
        generateAndDownloadSelfDeclaration(vendorData);
      }
      else if (agreement.id === 'overseas-agreement-basic') {
        generateAndDownloadOverseasAgreement(vendorData);
      }
      // if (agreement.is_basic) {
      // For basic agreements, generate PDF directly on frontend
      // generateAgreementPDF(vendor);
      // } else {
      // For detailed agreements, generate PDF directly on frontend
      // generateDetailedAgreementPDF(agreement, vendor);
      // }


    } catch (err) {
      console.error('Failed to generate PDF:', err);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setLoadingData(false);
    }
  };

  const handleViewSignature = async (agreement) => {
    setLoadingData(true);
    try {
      let data;

      if (agreement.is_basic) {
        const signatureImageUrl = await fetchDigitalSignature(vendor.id);

        // For basic agreements, create signature data from registration info
        data = {
          agreement_id: agreement.id,
          agreement_title: agreement.title,
          signature_details: {
            signed_by: agreement.signed_by || vendor.contact_person_name,
            signed_date: agreement.signed_date || vendor.created_at || vendor.registration_date,
            signature_method: "Digital Registration",
            certificate_authority: "PICLCompliance System",
            signature_verified: true,
            timestamp: vendor.created_at || vendor.registration_date || new Date().toISOString(),
            signature_image_url: signatureImageUrl
          },
          vendor_info: {
            company_name: vendor.company_name,
            vendor_code: vendor.vendor_code,
            contact_person: vendor.contact_person_name
          }
        };
      } else {
        // For detailed agreements, fetch from API
        const signatureResponse = await fetch(`${API_BASE_URL}/vendors/${vendor.id}/agreement-details/${agreement.id}/signature`, {
          credentials: 'include', // Include cookies for authentication
        });
        if (!signatureResponse.ok) {
          throw new Error(`HTTP error! status: ${signatureResponse.status}`);
        }
        const responseData = await signatureResponse.json();
        // Handle different response structures
        data = responseData?.data || responseData;
      }

      setSignatureData(data);
      setSignatureModal({ isOpen: true, agreement });
    } catch (err) {
      console.error('Failed to load signature details:', err);
      toast.error('Failed to load signature details. Please try again.');
    } finally {
      setLoadingData(false);
    }
  };

  const closeModal = () => {
    setViewAgreementModal({ isOpen: false, agreement: null });
    setSignatureModal({ isOpen: false, agreement: null });
    setAgreementData(null);
    setSignatureData(null);
  };

  if (loading) {
    return (
      <Card className="border border-slate-200 shadow-sm bg-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-600"></div>
              <span className="text-slate-600">Loading agreements...</span>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border border-red-200 bg-red-50">
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Icon name="AlertCircle" size={48} className="text-red-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Failed to Load Agreements</h3>
              <p className="text-slate-600 mb-4">{error}</p>
              <Button
                variant="outline"
                onClick={() => window.location.reload()}
                iconName="RefreshCw"
                iconPosition="left"
              >
                Try Again
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const agreementStats = {
    total: agreements.length,
    signed: agreements.filter(item => item.status === 'Signed').length,
    pending: agreements.filter(item => item.status === 'Pending Signature').length,
    expiring: agreements.filter(item => isExpiringSoon(item.valid_until)).length
  };

  return (
    <div className="space-y-5">
      {/* Agreement Types Info */}
      {agreements.some(ag => ag.is_basic) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900 mb-1">Agreement Types</h4>
              <p className="text-sm text-blue-800">
                <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200 mr-2">
                  Registration
                </span>
                agreements were signed during vendor registration. Additional detailed agreements can be added separately.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Agreement Overview */}
      <Card className="border border-slate-200 shadow-sm bg-white">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Icon name="FileSignature" size={20} className="text-slate-600" />
            Agreement Overview
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-semibold text-slate-900 mb-1">{agreementStats.total}</div>
              <div className="text-sm text-slate-600">Total Agreements</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold text-emerald-700 mb-1">{agreementStats.signed}</div>
              <div className="text-sm text-slate-600">Signed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold text-amber-700 mb-1">{agreementStats.pending}</div>
              <div className="text-sm text-slate-600">Pending</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold text-indigo-700 mb-1">{agreementStats.expiring}</div>
              <div className="text-sm text-slate-600">Expiring Soon</div>
            </div>
          </div>
        </CardContent>
      </Card>



      {/* Agreements List */}
      {agreements.length === 0 ? (
        <Card className="border border-slate-200 shadow-sm bg-white">
          <CardContent className="p-6">
            <div className="text-center py-12">
              <Icon name="FileText" size={48} className="text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No Agreements Found</h3>
              <p className="text-slate-600 mb-4">This vendor doesn't have any agreements yet.</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {agreements.map((agreement) => (
            <Card key={agreement.id} className="border border-slate-200 shadow-sm bg-white">
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2 flex-wrap">
                          <h4 className="text-lg font-semibold text-slate-900">{agreement.title}</h4>
                          {agreement.is_basic && (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                              Registration
                            </span>
                          )}
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(agreement.type)}`}>
                            {agreement.type}
                          </span>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(agreement.status)}`}>
                            {agreement.status}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 mb-3">{agreement.description}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                      {agreement.version && (
                        <div>
                          <label className="text-sm font-medium text-slate-600">Version</label>
                          <p className="text-sm text-slate-900 font-medium">v{agreement.version}</p>
                        </div>
                      )}
                      {agreement.document_size && (
                        <div>
                          <label className="text-sm font-medium text-slate-600">Document Size</label>
                          <p className="text-sm text-slate-900">{agreement.document_size}</p>
                        </div>
                      )}
                      {agreement.last_modified && (
                        <div>
                          <label className="text-sm font-medium text-slate-600">Last Modified</label>
                          <p className="text-sm text-slate-900">{formatDate(agreement.last_modified)}</p>
                        </div>
                      )}
                      {agreement.signed_date && (
                        <div>
                          <label className="text-sm font-medium text-slate-600">Signed Date</label>
                          <p className="text-sm text-slate-900">{formatDate(agreement.signed_date)}</p>
                        </div>
                      )}
                      {agreement.signed_by && (
                        <div>
                          <label className="text-sm font-medium text-slate-600">Signed By</label>
                          <p className="text-sm text-slate-900">{agreement.signed_by}</p>
                        </div>
                      )}
                      <div>
                        <label className="text-sm font-medium text-slate-600">Valid Until</label>
                        <p className={`text-sm font-medium ${isExpiringSoon(agreement.valid_until) ? 'text-amber-700' : 'text-slate-900'
                          }`}>
                          {agreement.valid_until}
                          {isExpiringSoon(agreement.valid_until) && (
                            <span className="ml-1 text-xs">(Soon)</span>
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Agreement Properties */}
                    <div className="flex flex-wrap gap-4 mb-4">
                      {agreement.witness_required && (
                        <div className="flex items-center space-x-2">
                          <Icon name="Users" size={16} className="text-slate-600" />
                          <span className="text-sm text-slate-600">Witness Required</span>
                        </div>
                      )}
                      {agreement.auto_renewal && (
                        <div className="flex items-center space-x-2">
                          <Icon name="RefreshCw" size={16} className="text-slate-600" />
                          <span className="text-sm text-slate-600">Auto Renewal</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3 pt-4 border-t border-slate-200">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Eye"
                    iconPosition="left"
                    onClick={() => handleViewAgreement(agreement)}
                    disabled={loadingData}
                  >
                    {loadingData && viewAgreementModal.agreement?.id === agreement.id ? 'Loading...' : 'View Agreement'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Download"
                    iconPosition="left"
                    onClick={() => handleDownloadPDF(agreement)}
                    disabled={loadingData}
                  >
                    {loadingData ? 'Generating...' : 'Download PDF'}
                  </Button>
                  {agreement.status === 'Pending Signature' && (
                    <Button variant="default" size="sm" iconName="PenTool" iconPosition="left">
                      Sign Agreement
                    </Button>
                  )}
                 

                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Digital Signature Information */}
      <Card className="border border-slate-200 shadow-sm bg-white">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Icon name="Shield" size={20} className="text-slate-600" />
            Digital Signature Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-slate-600">Signature Method:</span>
                <span className="font-medium text-slate-900">Digital Certificate</span>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-slate-600">Signature Verification:</span>
                <div className="flex items-center space-x-2">
                  <Icon name="CheckCircle" size={16} className="text-emerald-600" />
                  <span className="font-medium text-emerald-700">Verified</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* View Agreement Modal */}
      {viewAgreementModal.isOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={closeModal} />
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <div className="bg-surface border border-border rounded-lg shadow-medium w-full max-w-4xl max-h-[calc(90vh/var(--app-scale))] overflow-hidden">
                <div className="flex items-center justify-between p-6 border-b border-border">
                  <div>
                    <h2 className="text-xl font-semibold text-foreground">
                      {viewAgreementModal.agreement?.title}
                    </h2>
                    <div className="text-sm text-text-secondary mt-1">
                      {viewAgreementModal.agreement?.is_basic ? 'Registration Agreement' : 'Detailed Agreement'}
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={closeModal}>
                    <Icon name="X" size={20} />
                  </Button>
                </div>
                <div className="p-6 overflow-y-auto max-h-[calc(60vh/var(--app-scale))]">
                  {viewAgreementModal.agreement?.is_basic ? (
                    <div className="space-y-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-start space-x-3">
                          <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
                          <div>
                            <h4 className="font-medium text-blue-900 mb-1">Registration Agreement</h4>
                            <p className="text-sm text-blue-800">
                              This agreement was signed during vendor registration. The full agreement terms are part of the registration process.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-text-secondary">Agreement Type</label>
                          <p className="text-foreground mt-1">{viewAgreementModal.agreement?.type}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-text-secondary">Status</label>
                          <p className="text-foreground mt-1">{viewAgreementModal.agreement?.status}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-text-secondary">Signed Date</label>
                          <p className="text-foreground mt-1">{formatDate(viewAgreementModal.agreement?.signed_date)}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-text-secondary">Signed By</label>
                          <p className="text-foreground mt-1">{viewAgreementModal.agreement?.signed_by}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-text-secondary">Valid Until</label>
                          <p className="text-foreground mt-1">{viewAgreementModal.agreement?.valid_until}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-text-secondary">Description</label>
                          <p className="text-foreground mt-1">{viewAgreementModal.agreement?.description}</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {agreementData ? (
                        <>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                            <div>
                              <label className="text-sm font-medium text-text-secondary">Agreement Type</label>
                              <p className="text-foreground mt-1">{agreementData.type}</p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-text-secondary">Status</label>
                              <p className="text-foreground mt-1">{agreementData.status}</p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-text-secondary">Version</label>
                              <p className="text-foreground mt-1">{agreementData.version}</p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-text-secondary">Document Size</label>
                              <p className="text-foreground mt-1">{agreementData.document_size}</p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-text-secondary">Signed Date</label>
                              <p className="text-foreground mt-1">{formatDate(agreementData.signed_date)}</p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-text-secondary">Signed By</label>
                              <p className="text-foreground mt-1">{agreementData.signed_by}</p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-text-secondary">Valid Until</label>
                              <p className="text-foreground mt-1">{agreementData.valid_until}</p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-text-secondary">Last Modified</label>
                              <p className="text-foreground mt-1">{formatDate(agreementData.last_modified)}</p>
                            </div>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-text-secondary">Description</label>
                            <p className="text-foreground mt-1">{agreementData.description}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-text-secondary">Document Content</label>
                            <div className="mt-2 p-4 bg-muted rounded-lg border">
                              <p className="text-sm text-foreground whitespace-pre-wrap">{agreementData.document_content}</p>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="flex items-center justify-center py-8">
                          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
                  <Button variant="outline" onClick={closeModal}>
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Signature Modal */}
      {signatureModal.isOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={closeModal} />
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <div className="bg-surface border border-border rounded-lg shadow-medium w-full max-w-2xl max-h-[calc(90vh/var(--app-scale))] overflow-hidden">
                <div className="flex items-center justify-between p-6 border-b border-border">
                  <div>
                    <h2 className="text-xl font-semibold text-foreground">
                      Signature Details
                    </h2>
                    <div className="text-sm text-text-secondary mt-1">
                      {signatureModal.agreement?.title}
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={closeModal}>
                    <Icon name="X" size={20} />
                  </Button>
                </div>
                <div className="p-6 overflow-y-auto max-h-[calc(60vh/var(--app-scale))]">
                  {signatureData ? (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-text-secondary">Signed By</label>
                          <p className="text-foreground mt-1">{signatureData.signature_details?.signed_by || signatureData.signed_by || '-'}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-text-secondary">Signed Date</label>
                          <p className="text-foreground mt-1">{formatDate(signatureData.signature_details?.signed_date || signatureData.signed_date)}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-text-secondary">Signature Method</label>
                          <p className="text-foreground mt-1">{signatureData.signature_details?.signature_method || signatureData.signature_method || '-'}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-text-secondary">Certificate Authority</label>
                          <p className="text-foreground mt-1">{signatureData.signature_details?.certificate_authority || signatureData.certificate_authority || '-'}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-text-secondary">Verification Status</label>
                          <div className="flex items-center space-x-2 mt-1">
                            <Icon name="CheckCircle" size={16} className="text-success" />
                            <span className="text-success font-medium">Verified</span>
                          </div>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-text-secondary">Timestamp</label>
                          <p className="text-foreground mt-1">{formatDate(signatureData.signature_details?.timestamp || signatureData.timestamp)}</p>
                        </div>
                      </div>
                      <div className="bg-muted rounded-lg p-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <Icon name="FileSignature" size={20} className="text-primary" />
                          <h4 className="font-medium text-foreground">Digital Signature</h4>
                        </div>
                        {(
                          signatureData.signature_details?.signature_image_url ||
                          signatureData.signature_image_url
                        ) && (
                          <div className="mb-3 p-2 bg-white rounded-md border border-border inline-block">
                            <img
                              src={
                                signatureData.signature_details?.signature_image_url ||
                                signatureData.signature_image_url
                              }
                              alt="Digital signature"
                              className="max-h-24 object-contain"
                            />
                          </div>
                        )}
                        <p className="text-sm text-text-secondary">
                          This agreement has been digitally signed using a secure certificate from {signatureData.signature_details?.certificate_authority || signatureData.certificate_authority || 'PICLCompliance System'}.
                          The signature has been verified and is legally binding.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center py-8">
                      <Icon name="Loader2" size={32} className="animate-spin text-primary" />
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
                  <Button variant="outline" onClick={closeModal}>
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

    </div>
  );
};

export default AgreementsTab;